import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { config } from 'config';

import { imgSize400 } from 'constants/image';
import { formatImagelink } from 'utils';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import { formatOfferPrice } from 'utils/price';
import type { WithTestId } from 'types/app';

import { adjustOfferCardFavoriteCount } from '@Modules/offers/slice';
import { AnalyticsTypes } from '@Modules/analytics/types';
import { selectLanguage } from '@Modules/app/selectors';
import { selectUser } from '@Modules/auth/selector';
import type { OfferType } from '@Modules/offers/types';

import { SellerContact } from '@Atomic/organisms/SellerContact';
import { ButtonFavoriteConnected } from '@Atomic/molecules/ButtonFavoriteConnected';
import { Button } from '@Atomic/atoms/Button';
import { useCopyToClipboard } from '@Atomic/atoms/CopyButton/hook';
import { Icons } from '@Atomic/atoms/Icons';
import { Image } from '@Atomic/atoms/Image';
import { Text } from '@Atomic/atoms/Text';

import styles from './offerTop.module.scss';

type OfferTopProps = WithTestId<{
  offer: OfferType;
  withContacts?: boolean;
  isExpired?: boolean;
  variant?: 'default' | 'compact' | 'actionsOnly';
  className?: string;
  onGoBack?: () => void;
}>;

export const OfferTop: FC<OfferTopProps> = ({
  offer,
  withContacts = false,
  isExpired = false,
  variant = 'default',
  className,
  onGoBack,
  testId = 'offer-top',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { copyToClipboard, isCopied } = useCopyToClipboard(1000);

  const language = useSelector(selectLanguage);
  const user = useSelector(selectUser);

  const photo = offer.primaryPhoto ?? offer.photos[0];
  const offerImage = photo ? formatImagelink(photo.upload.cropUrlTemplate, imgSize400) : '';
  const offerImageAlt = photo?.alt || offer.title;

  const shouldShowContacts = withContacts && !isExpired;

  const priceTitle = formatOfferPrice(offer.price, offer.priceType, t);

  // Favorite counter is shown next to the heart button only in the desktop sidebar
  // (actionsOnly) and in the mobile card header (compact). In the sticky header on
  // scroll (default) the counter is hidden — per BP-6651 designs.
  const showFavoriteCounter = !isExpired && variant !== 'default' && offer.inFavoriteCount > 0;

  // BP-6651: counter should not change when user toggles favorite on their own offer.
  const isOwnOffer = !!user?.uuid && user.uuid === offer.seller.uuid;

  const handleFavoriteToggle = (willBeFavorited: boolean) => {
    if (isOwnOffer) {
      return;
    }

    dispatch(adjustOfferCardFavoriteCount({ delta: willBeFavorited ? 1 : -1 }));
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        styles.offerTop,
        variant !== 'default' && styles[`variant-${variant}`],
        {
          [styles.expired]: isExpired,
        },
        className
      )}
    >
      <div className={styles.topLeftSlot}>
        <Button
          className={styles.backButton}
          variant="text"
          size="extra-small"
          onClick={onGoBack}
          iconLeft={<Icons.ArrowLeft />}
          testId={getTestIdByParts(testId, 'back-button')}
        />

        <div className={styles.offerImageWrapper}>
          <Image src={offerImage} alt={offerImageAlt} />
        </div>
      </div>

      <div className={styles.topCenterSlot}>
        <div className={styles.info}>
          <Text className={styles.title}>
            {isExpired ? t('offer.expiredOfferMessage') : priceTitle}
          </Text>
          <Text className={styles.description}>{offer.title}</Text>
        </div>
      </div>

      <div className={cn(styles.topRightSlot)}>
        {shouldShowContacts && (
          <SellerContact
            className={styles.contacts}
            buttonClassName={styles.contactButton}
            offerId={offer.id}
            seller={offer.seller}
            contactViaApp={offer.contactViaApp}
            testId={getTestIdByParts(testId, 'contact')}
          />
        )}

        <div className={styles.favoriteGroup}>
          {showFavoriteCounter && (
            <Text
              className={styles.favoriteCounter}
              testId={getTestIdByParts(testId, 'favorite-counter')}
            >
              {offer.inFavoriteCount}
            </Text>
          )}

          <ButtonFavoriteConnected
            offerId={offer.id}
            isOfferFavorite={offer.favorited}
            label={undefined}
            variant="black"
            testId={getTestIdByParts(testId, 'favorite-button')}
            onToggle={handleFavoriteToggle}
            analyticsSource={AnalyticsTypes.FavoriteSource.OFFER_DETAILS}
            photo={photo}
          />
        </div>

        <button
          type="button"
          className={styles.shareButton}
          data-testid={getTestIdByParts(testId, 'share-button')}
          aria-label={t('buttons.share')}
          onClick={
            isCopied
              ? undefined
              : () => copyToClipboard(`${config.ENV.ORIGIN_URL}/${offer.webUriInfo[language]}`)
          }
        >
          {isCopied ? <Icons.Check /> : <Icons.ShareiOS />}
        </button>
      </div>
    </div>
  );
};
